import { useContext, useState, useEffect } from "react";
import { PlannerContext } from "../../../../context/planner/PlannerContext";
import {
  TimeRangeFields,
  isTimeRangeValid,
  DeleteBlockSection,
  SaveBlockButton,
  ActivitySelector,
} from "./shared";
import { Stack, Box, useMediaQuery, useTheme } from "@mui/material";

/**
 * Calculate the duration in minutes between two time strings (HH:MM format)
 */
function calculateBlockDurationMinutes(startTime, endTime) {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
    return null;
  }
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  const duration = endTotalMinutes - startTotalMinutes;
  return duration > 0 ? duration : null;
}

const EditActivityBlock = ({ block }) => {
  const {
    updateBlock,
    deleteBlock,
    activities,
    setSelectedBlockId,
    setSelectedTool,
    closeToolPanel,
  } = useContext(PlannerContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activityId, setActivityId] = useState(block.activityId || "");
  const [startTime, setStartTime] = useState(block.startTime);
  const [endTime, setEndTime] = useState(block.endTime);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when block changes
  useEffect(() => {
    setActivityId(block.activityId || "");
    setStartTime(block.startTime);
    setEndTime(block.endTime);
    setHasChanges(false);
  }, [block.id, block.activityId, block.startTime, block.endTime]);

  // Track changes
  useEffect(() => {
    const changed =
      activityId !== (block.activityId || "") ||
      startTime !== block.startTime ||
      endTime !== block.endTime;
    setHasChanges(changed);
  }, [activityId, startTime, endTime, block]);

  const handleSave = () => {
    const selectedActivity = activities.find((a) => a.id === activityId);
    updateBlock(block.id, {
      name: selectedActivity?.name || "Activity",
      activityId,
      startTime,
      endTime,
    });
    setHasChanges(false);
    setSelectedBlockId(null);
    setSelectedTool("dayConfig");
    if (isMobile) {
      closeToolPanel();
    }
  };

  const handleDelete = () => {
    deleteBlock(block.id);
    if (isMobile) {
      closeToolPanel();
    }
  };

  const handleActivityChange = (newActivityId) => {
    setActivityId(newActivityId);
  };

  const timesValid = isTimeRangeValid(startTime, endTime);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          pb: 2,
        }}
      >
        <Stack spacing={2} sx={{ py: 2 }}>
          <ActivitySelector
            activities={activities}
            value={activityId}
            onChange={handleActivityChange}
            blockDurationMinutes={calculateBlockDurationMinutes(
              startTime,
              endTime,
            )}
          />

          <TimeRangeFields
            startTime={startTime}
            endTime={endTime}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
          />

          {!isMobile && (
            <>
              <SaveBlockButton
                onSave={handleSave}
                disabled={!hasChanges || !timesValid}
              />

              <DeleteBlockSection onDelete={handleDelete} showLabel={false} />
            </>
          )}
        </Stack>
      </Box>

      {isMobile && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <SaveBlockButton
            onSave={handleSave}
            disabled={!hasChanges || !timesValid}
          />
          <DeleteBlockSection
            onDelete={handleDelete}
            showDivider={false}
            showLabel={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default EditActivityBlock;
